<div class="user-create">
  <div class="user-create__content">
    <div class="user-create__layout">
      <div class="user-create__header">
        <div class="user-create__heading">
          <h1 class="page-title">Create User</h1>
          <p class="page-subtitle">Add a new user and assign the right role.</p>
        </div>
        <div class="user-create__actions">
          <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
          <app-button type="button" [disabled]="isSaving" (click)="saveUser()">
            {{ isSaving ? "Creating..." : "Create user" }}
          </app-button>
        </div>
      </div>

      <app-card>
        <form id="profileForm" [formGroup]="profileForm" class="user-create__form">
          <div class="user-create__form-field user-create__form-field--full-width">
            <div class="user-create__avatar-section">
              <app-avatar
                class="user-create__avatar-preview"
                [imageUrl]="avatarImageUrl"
                [name]="avatarName"
                size="2xl"></app-avatar>
              <div class="user-create__avatar-details">
                <p class="user-create__avatar-name">{{ avatarName }}</p>
                <p class="user-create__avatar-meta">{{ avatarEmail }}</p>
              </div>
              <div class="user-create__avatar-actions">
                <input type="file" #avatarInput accept="image/*" (change)="onAvatarChange($event)" style="display: none" />
                <app-button type="button" variant="secondary" (click)="avatarInput.click()"> Change Image </app-button>
                @if (avatarImageUrl) {
                <app-button type="button" variant="secondary" (click)="removeAvatar()"> Remove Image </app-button>
                }
              </div>
            </div>
          </div>

          <div class="user-create__form-grid">
            <div class="user-create__form-field user-create__form-field--spaced">
              <div class="user-create__form-label-row">
                <ng-glyph class="user-create__form-label-icon" name="person"></ng-glyph>
                <label for="name" class="user-create__form-label">Name</label>
              </div>
              <input
                id="name"
                type="text"
                formControlName="name"
                class="user-create__form-input"
                [class.user-create__form-input--error]="profileForm.get('name')?.invalid && profileForm.get('name')?.touched" />
              @if (profileForm.get('name')?.invalid && profileForm.get('name')?.touched) {
              <div class="user-create__form-error">Name is required and must be at least 2 characters</div>
              }
            </div>

            <div class="user-create__form-field user-create__form-field--spaced">
              <div class="user-create__form-label-row">
                <ng-glyph class="user-create__form-label-icon" name="mail"></ng-glyph>
                <label for="email" class="user-create__form-label">Email</label>
              </div>
              <input
                id="email"
                type="email"
                formControlName="email"
                class="user-create__form-input"
                [class.user-create__form-input--error]="profileForm.get('email')?.invalid && profileForm.get('email')?.touched" />
              @if (profileForm.get('email')?.invalid && profileForm.get('email')?.touched) {
              <div class="user-create__form-error">A valid email is required</div>
              }
            </div>

            <div class="user-create__form-field user-create__form-field--spaced">
              <div class="user-create__form-label-row">
                <ng-glyph class="user-create__form-label-icon" name="call"></ng-glyph>
                <label for="phone" class="user-create__form-label">Phone</label>
              </div>
              <input id="phone" type="tel" formControlName="phone" class="user-create__form-input" />
            </div>

            <div class="user-create__form-field user-create__form-field--spaced">
              <div class="user-create__form-label-row">
                <ng-glyph class="user-create__form-label-icon" name="group"></ng-glyph>
                <label for="role" class="user-create__form-label">Role</label>
              </div>
              <app-autocomplete
                [prefetchOnInit]="true"
                id="role"
                placeholder="Search roles..."
                [options]="roleOptions"
                [selectedValue]="profileForm.get('role')?.value"
                (selectionChange)="onRoleChange($event)"
                [class.user-create__form-input--error]="profileForm.get('role')?.invalid && profileForm.get('role')?.touched">
              </app-autocomplete>
              @if (profileForm.get('role')?.invalid && profileForm.get('role')?.touched) {
              <div class="user-create__form-error">Role is required.</div>
              }
            </div>

            <div class="user-create__form-field user-create__form-field--spaced">
              <div class="user-create__form-label-row">
                <ng-glyph class="user-create__form-label-icon" name="location_on"></ng-glyph>
                <label for="address" class="user-create__form-label">Address</label>
              </div>
              <input id="address" type="text" formControlName="address" class="user-create__form-input" />
            </div>
          </div>
        </form>
      </app-card>

      <app-card class="user-create__password-card">
        <form [formGroup]="passwordForm" class="user-create__form">
          <div class="user-create__section-header">
            <div>
              <h2 class="user-create__section-title">Set password</h2>
              <p class="user-create__section-hint">Use a strong password with at least 8 characters.</p>
            </div>
          </div>

          <div class="user-create__form-grid">
            <div class="user-create__form-field user-create__form-field--spaced">
              <div class="user-create__form-label-row">
                <ng-glyph class="user-create__form-label-icon" name="key"></ng-glyph>
                <label for="password" class="user-create__form-label">Password</label>
              </div>
              <input
                id="password"
                type="password"
                formControlName="password"
                class="user-create__form-input"
                [class.user-create__form-input--error]="passwordForm.get('password')?.invalid && passwordForm.get('password')?.touched" />
              @if (passwordForm.get('password')?.invalid && passwordForm.get('password')?.touched) {
              <div class="user-create__form-error">Password must be at least 8 characters.</div>
              }
            </div>

            <div class="user-create__form-field user-create__form-field--spaced">
              <div class="user-create__form-label-row">
                <ng-glyph class="user-create__form-label-icon" name="key"></ng-glyph>
                <label for="confirmPassword" class="user-create__form-label">Confirm password</label>
              </div>
              <input
                id="confirmPassword"
                type="password"
                formControlName="confirmPassword"
                class="user-create__form-input"
                [class.user-create__form-input--error]="passwordForm.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched" />
              @if (passwordForm.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched) {
              <div class="user-create__form-error">Passwords do not match.</div>
              }
            </div>
          </div>
        </form>
      </app-card>
    </div>
  </div>
</div>
